import { makeAutoObservable, runInAction } from "mobx";
import ShipByReturnGoService, { IEligibleReturnMethod, IEligibleReturnMethodCategory, IEligibleReturnMethodsResponseSettings, IWalletPromotion } from "../../services/api/ship-by-returngo-service";
import { rootContainer, TYPE } from "../../services/container/root";
import { IOption } from "../../components/inputs/select-dropdown/select-dropdown";
import carrierSelectionOptions from "./carrier-selection-options";
import { ICountry } from "../../services/api/return-methods-service";
import { isSet } from "../../models/model-types-methods";
import AggregatorsInfoStore from "../return-methods/stores/aggregators-info-store";
import { SHIPPING_CONNECTION_CODE } from "../../models/IntegrationsTypes";
import storeSBRPageMixPanel, { IMixpanelRMData, SBR_PAGE_MIXPANEL_EVENT } from "./sbr-page-mixpanel-store";

export interface ISbrPageTabsElementsWidths {
    containerWidth?: number;
    refTabs?: React.RefObject<HTMLDivElement>;
}

function areAllWidthsSet(widths: ISbrPageTabsElementsWidths): boolean {
    return isSet(widths.containerWidth) &&
           isSet(widths.refTabs?.current);
}

const TOP_COUNTRIES = ['US', 'CA', 'GB', 'DE', 'FR', 'IT', 'ES', 'NL', 'AU', 'JP'];


class ShipByReturnGoStore {
    private _aggregatorsInfoStore?: AggregatorsInfoStore;
    private _eligibleReturnMethodCategories: IEligibleReturnMethodCategory[] = [];
    private _converted_return_methods: IEligibleReturnMethod[] = [];
    private _failed_to_convert: IEligibleReturnMethod[] = [];
    private _selectedReturnMethods: IEligibleReturnMethod[] = [];
    private _settings: IEligibleReturnMethodsResponseSettings|undefined;
    private _isLoading: boolean = false;
    private _isUpdating: boolean = false;
    private _selectedCountryCode: string = 'US';
    private _countries: ICountry[] = [];
    private _lastVisibleCountryTabIndex: number = 5;
    
    private _tabsElementsWidths: ISbrPageTabsElementsWidths = {};
    private _tabsUIChangeTimeout: NodeJS.Timeout|undefined;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get service(): ShipByReturnGoService {
        return rootContainer.get<ShipByReturnGoService>(TYPE.ShipByReturnGoService);
    }

    public get isLoading(): boolean { return this._isLoading; }
    public get isUpdating(): boolean { return this._isUpdating; }

    public get selectedCountryCode(): string { return this._selectedCountryCode; }
    
    public get countries() { return this._countries; }
        
    public get availableCountriesOptions(): IOption[] { 
        return this._countries
            .filter((_, index) => index > this._lastVisibleCountryTabIndex) // Filter out countries that are not visible in the tabs
            .map(country => ({ value: country.code, label: country.name})); 
    }
    public get selectedCountryOption(): IOption|undefined {
        return this.availableCountriesOptions.find(o => o.value === this.selectedCountryCode);
    }

    public get convertedReturnMethods(): IEligibleReturnMethod[] { return this._converted_return_methods; }
    public get failedRoConvert(): IEligibleReturnMethod[] { return this._failed_to_convert; }

    public isRmSelected(rm: IEligibleReturnMethod): boolean { return this._selectedReturnMethods.includes(rm); }
    public toggleRmSelected(rm: IEligibleReturnMethod): void {
        const index = this._selectedReturnMethods.indexOf(rm);
        if (index !== -1) {
            this._selectedReturnMethods.splice(index, 1);
        } else {
            this._selectedReturnMethods.push(rm);
        }
    }

    public get returnMethodCategories(): IEligibleReturnMethodCategory[] {
        return this._eligibleReturnMethodCategories;
    }

    public get selectedReturnMethods(): IEligibleReturnMethod[] { return this._selectedReturnMethods; }
    public getSelectedReturnMethodsData(getTranslation: (k: string) => string): IMixpanelRMData[] {
        const data: IMixpanelRMData[] = [];

        this.returnMethodCategories.forEach(rmCategory => {
            // filter selected method among all eligible and push to expected by mixpanel structure
            rmCategory.list.filter(rm => this.selectedReturnMethods.some(srm => srm === rm)).forEach(rm => {
                data.push({
                    Return_method_name: getTranslation(rm.name),
                    Return_method_type: rmCategory.category,
                    Shipping_connection: this.getCurrentShippingConnection(rm),
                    Linked_policies: rm.policy_rules_count,
                    Updated_to_SBR: this._converted_return_methods.some(converted_rm => converted_rm.id === rm.id),
                });
            });
        });

        return data;
    }

    public get walletPromotions(): IWalletPromotion[] { return this._settings?.wallet_promotions ?? []; }
    public get walletActivated(): boolean { return this._settings?.is_wallet_activated ?? false; }

    public setSelectedCountryCode(countryCode: string): void {
        this._selectedCountryCode = countryCode;
    }

    public getAvailableCarriers() {
        const availableCarriers = carrierSelectionOptions.filter(carrier => 
            carrier.list.find(service => service.countries.includes(this.selectedCountryCode))
        );
        
        // Filter unique carriers based on getBaseCarrierCode
        const uniqueCarriers = availableCarriers.reduce((acc, carrier) => {
            const baseCarrierCode = this.getBaseCarrierCode(carrier.carrierCode);
            const existingCarrier = acc.find(c => this.getBaseCarrierCode(c.carrierCode) === baseCarrierCode);
            
            if (!existingCarrier) {
                acc.push(carrier);
            }
            
            return acc;
        }, [] as typeof availableCarriers);
        
        return uniqueCarriers;
    }

    public getBaseCarrierCode(carrierCode: string): string {
        switch (carrierCode) {
            case 'DHL_Express':
            case 'DHL_DE':
                return 'DHL';
            case 'DPD_FR':
            case 'DPD_DE':
            case 'DPD_AT':
                return 'DPD';
            case 'InPost_IT':
            case 'InPost_ES':
                return 'InPost';
            default:
                return carrierCode;
        }
    }

    async convertRMsToSBR(onCloseClicked: () => void, showSetUpNowSuccessModal: () => void, getTranslation: (k: string) => string) {
        this._isUpdating = true;
        try {
            const res = await this.service.convertRMsToSBR(this._selectedReturnMethods);
            runInAction(() => {
                this._isUpdating = false;
                this._converted_return_methods = res.converted_return_methods;
                this._failed_to_convert = res.failed_to_convert;
                
                /** If at least one RM was converted - open SetUpNowSuccessModal */
                if (this.convertedReturnMethods.length > 0) {
                    onCloseClicked();
                    showSetUpNowSuccessModal();
                } 
                else {
                    this.registerMixpanelAdoptionEvent(getTranslation, 'Error');
                }
            });
        } catch (e) {
            runInAction(() => {
                this._isUpdating = false;
                this.registerMixpanelAdoptionEvent(getTranslation, 'Error');
            });
        }
    }

    public registerMixpanelAdoptionEvent(getTranslation: (k: string) => string, status: string, walletPromotionClicked: boolean = false): void {
        storeSBRPageMixPanel.trackEvent(SBR_PAGE_MIXPANEL_EVENT.ADOPTION, {
            Status: status,
            Return_Method: this.getSelectedReturnMethodsData(getTranslation),
            Wallet_Promotion_Clicked: walletPromotionClicked,
        });
    }

    public getCurrentShippingConnection = (returnMethod: IEligibleReturnMethod): string => {
        let aggregatorCode: SHIPPING_CONNECTION_CODE|undefined = returnMethod.return_label_setting?.shipping_connection?.aggregator_code;
        return this._aggregatorsInfoStore?.getAggregtorLabel(aggregatorCode) ?? '-';
    };

    async load() {
        this._isLoading = true;
        try {
            const res = await this.service.getEligibleReturnMethods();
            runInAction(() => {
                this._isLoading = false;
                this._eligibleReturnMethodCategories = res.eligible_return_methods;
                this._selectAllEligibleReturnMethods();
                this._settings = res.settings;  
                this._aggregatorsInfoStore = new AggregatorsInfoStore(this._settings.aggregators_info);              
                this._countries = constructCountryList(this._settings.countries_list, extractCountryCodesFromCarrierSelectionOptions())

                storeSBRPageMixPanel.setIdentity(this._settings.identity);
                storeSBRPageMixPanel.startMixpanelSessionRecording();
            });
        } catch (e) {
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public isCountryTabVisible(index: number): boolean {
        return index <= this._lastVisibleCountryTabIndex;
    }

    public onTabsElementsWidthsChanged(widths: ISbrPageTabsElementsWidths): void {
        this._tabsElementsWidths = { ...this._tabsElementsWidths, ...widths };
        
        /** ignore while not all values are set */
        if (!areAllWidthsSet(this._tabsElementsWidths)) {
            return;
        }

        /** implementing debouncing, couldn't make it work with lodash debounce method */
        if (this._tabsUIChangeTimeout) {
            clearTimeout(this._tabsUIChangeTimeout);
        }
        this._tabsUIChangeTimeout = setTimeout(() => {
            this._calculateLastVisibleCountryTabIndex();
        }, 50);
    }

    private _calculateLastVisibleCountryTabIndex(): void {
        runInAction(() => {
            this._tabsUIChangeTimeout = undefined;
            if (!areAllWidthsSet(this._tabsElementsWidths)) {
                return;
            }

            const { containerWidth, refTabs } = this._tabsElementsWidths;
            
            let witdhSum = 0;
            for (let i = 0; i < refTabs!.current!.children.length; i++) {
                const tab = refTabs!.current!.children[i] as HTMLDivElement;
                witdhSum += tab.offsetWidth;
                if (witdhSum <= containerWidth!) {
                    this._lastVisibleCountryTabIndex = i;
                }
            }
            this._lastVisibleCountryTabIndex = Math.max(this._lastVisibleCountryTabIndex, 2);   
        });
    }

    private _selectAllEligibleReturnMethods(): void {
        /** reset selected RMs list */
        this._selectedReturnMethods = [];
        this.returnMethodCategories.forEach(rmCategory => {
            rmCategory.list.forEach(rm => this._selectedReturnMethods.push(rm))
        })
    }
}

const shipByReturngoPageStore = new ShipByReturnGoStore();
export default shipByReturngoPageStore;



function constructCountryList(countries: ICountry[], carrierCountryCodes: string[]): ICountry[] {
    const sortedCountries: ICountry[] = [];
    const filteredCountries: ICountry[] = [];

    /** filter out countries that are not in carrier selection options */
    countries.forEach(country => {
        if (carrierCountryCodes.includes(country.code)) {
            filteredCountries.push(country);
        }
    });

    /** sort countries where first should be from TOP_COUNTRIES */
    TOP_COUNTRIES.forEach(topCountryCode => {
        const country = filteredCountries.find(c => c.code === topCountryCode);
        if (country) {
            sortedCountries.push(country);
            filteredCountries.splice(filteredCountries.indexOf(country), 1);
        }
    });

    return sortedCountries.concat(filteredCountries);
};


function extractCountryCodesFromCarrierSelectionOptions(): string[] {
    const allCountryCodes = carrierSelectionOptions
        .flatMap(carrier =>
            carrier.list.flatMap(service =>
                service.countries
            )
        );
    return Array.from(new Set(allCountryCodes));
}
